<template>
  <div class="driver-deliver-container">
    <div v-if="loading" class="loading-state">
      <v-progress-circular
        indeterminate
        color="green"
        :size="64"
      />
      <p class="mt-3">{{ $t('driverDeliver.loadingOrderDetails') }}</p>
    </div>

    <div v-else-if="error && !order" class="error-state">
      <v-icon color="red" size="64">error</v-icon>
      <h2 class="mt-3">{{ $t('common.error') }}</h2>
      <p>{{ error }}</p>
    </div>

    <template v-else-if="order">
      <div v-if="delivered" class="success-state">
        <v-icon color="green" size="80">check_circle</v-icon>
        <h2 class="mt-3 green--text">{{ $t('driverDeliver.orderMovedTitle') }}</h2>
        <p class="mt-2">{{ $t('driverDeliver.orderMovedMessage', { id: order.theOrderID }) }}</p>
      </div>

      <div v-if="!delivered && order.status_type === 'branch_delivery'" class="already-delivered-state">
        <v-icon color="blue" size="64">info</v-icon>
        <h2 class="mt-3">{{ $t('driverDeliver.alreadyAtBranchTitle') }}</h2>
        <p>{{ $t('driverDeliver.alreadyAtBranchMessage') }}</p>
      </div>

      <div v-if="!delivered && order.status_type !== 'branch_delivery' && order.status_type !== 'int_shipment'" class="wrong-status-state">
        <v-icon color="orange" size="64">warning</v-icon>
        <h2 class="mt-3">{{ $t('driverDeliver.cannotProcessTitle') }}</h2>
        <p>{{ $t('driverDeliver.wrongStatusMessage') }} <strong>{{ statusLabel }}</strong></p>
      </div>

      <div class="order-details-card">
        <v-card>
          <v-card-title class="headline">
            {{ $t('driverDeliver.orderNumberLabel', { id: order.theOrderID }) }}
          </v-card-title>

          <v-card-text>
            <v-list dense>
              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('columns.customerName') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.customer_name || $t('driverDeliver.naLabel') }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('columns.customerPhone') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.customer_phone || $t('driverDeliver.naLabel') }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('columns.productName') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.product_name || $t('driverDeliver.naLabel') }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('columns.quantity') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.quantity }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile v-if="order.size">
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('columns.size') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.size }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider v-if="order.size" />

              <v-list-tile v-if="order.color">
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('customer.color') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.color }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider v-if="order.color" />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('customer.itemPrice') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.item_price | usdFormat }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('columns.totalAmount') }}</v-list-tile-sub-title>
                  <v-list-tile-title class="font-weight-bold">{{ order.total_amount | usdFormat }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('columns.status') }}</v-list-tile-sub-title>
                  <v-list-tile-title>
                    <v-chip
                      :color="statusColor"
                      text-color="white"
                      small
                    >{{ statusLabel }}</v-chip>
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('customer.orderDate') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.order_date || $t('driverDeliver.naLabel') }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('columns.deliveryAddress') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.customer_address || $t('driverDeliver.naLabel') }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />

              <v-list-tile v-if="order.delivery_area">
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('driverDeliver.areaLabel') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.delivery_area }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider v-if="order.delivery_area" />

              <v-list-tile v-if="order.delivery_house_number">
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('driverDeliver.houseBuildingLabel') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.delivery_house_number }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider v-if="order.delivery_house_number" />

              <v-list-tile v-if="order.delivery_phone">
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('driverDeliver.deliveryPhoneLabel') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.delivery_phone }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider v-if="order.delivery_phone" />

              <v-list-tile>
                <v-list-tile-content>
                  <v-list-tile-sub-title>{{ $t('customer.deliveryType') }}</v-list-tile-sub-title>
                  <v-list-tile-title>{{ order.delivery_type || $t('driverDeliver.naLabel') }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-card-text>
        </v-card>
      </div>

      <div v-if="canDeliver" class="deliver-button-wrapper">
        <v-btn
          v-if="!confirming"
          block
          large
          color="green"
          dark
          class="deliver-button"
          @click="confirmDelivery"
        >
          <v-icon left large>local_shipping</v-icon>
          {{ $t('driverDeliver.tapToMoveButton') }}
        </v-btn>

        <v-btn
          v-else
          block
          large
          color="green"
          dark
          class="deliver-button"
          disabled
          loading
        >
          {{ $t('driverDeliver.processingButton') }}
        </v-btn>

        <p v-if="error" class="red--text mt-3 text-center">{{ error }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'

export default {
  data() {
    return {
      order: null,
      loading: true,
      confirming: false,
      delivered: false,
      error: null
    }
  },

  computed: {
    orderId() {
      return this.$route.params.order_id
    },

    statusLabel() {
      if (!this.order || !this.order.status_type) return this.$t('driverDeliver.unknownStatus')
      return this.order.status_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },

    statusColor() {
      if (!this.order) return 'grey'
      switch (this.order.status_type) {
        case 'int_shipment': return 'blue'
        case 'branch_delivery': return 'orange'
        case 'delivered': return 'green'
        case 'canceled': return 'red'
        case 'refunded': return 'purple'
        default: return 'grey'
      }
    },

    canDeliver() {
      return !this.delivered && this.order && this.order.status_type === 'int_shipment'
    }
  },

  mounted() {
    this.fetchOrderInfo()
  },

  methods: {
    fetchOrderInfo() {
      this.loading = true
      this.error = null
      let token = localStorage.getItem('access_token')

      axios
        .get(`api/driver_deliver_info/${this.orderId}`, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(response => {
          this.order = response.data
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.error = error.response.data.error || error.response.data.message || this.$t('driverDeliver.failedToLoadOrder')
          } else {
            this.error = this.$t('driverDeliver.failedToLoadOrderDetails')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    confirmDelivery() {
      this.confirming = true
      this.error = null
      let token = localStorage.getItem('access_token')

      axios
        .put(`api/driver_deliver/${this.orderId}`, {}, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(response => {
          this.delivered = true
          this.order.status_type = 'branch_delivery'
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.error = error.response.data.error || error.response.data.message || this.$t('driverDeliver.failedToProcessOrder')
            swal(this.$t('common.error'), this.error, 'error')
          } else {
            this.error = this.$t('invoice.somethingWentWrong')
            swal(this.$t('common.error'), this.error, 'error')
          }
        })
        .finally(() => {
          this.confirming = false
        })
    }
  }
}
</script>

<style scoped>
.driver-deliver-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 16px;
  padding-bottom: 160px;
  min-height: 100vh;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 24px;
}

.success-state,
.already-delivered-state,
.wrong-status-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 24px;
  margin-bottom: 16px;
}

.order-details-card {
  margin-bottom: 16px;
}

.deliver-button-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.deliver-button {
  height: 80px !important;
  font-size: 20px !important;
  font-weight: bold !important;
  letter-spacing: 1px;
  border-radius: 12px !important;
}

.text-center {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/driverDeliver.vue